import { Injectable } from '@angular/core'
import { Observable, of } from 'rxjs'
import { delay, map } from 'rxjs/operators'

import { AdminTeamService } from '@app/modules/admin/admin-team/admin-team.service'
import { ClientsService } from '@app/modules/admin/clients/clients.service'
import { UserGroupsService } from '@app/modules/client/config/user-groups/user-groups.service'
import { Geofence, GeofenceGroup, GeofenceStatus } from '@app/modules/client/shared/models/geofence.model'
import { GeofencesApiService } from '@app/modules/client/shared/services/geofences-api.service'
import { VehiclesService } from '@app/modules/client/vehicles/vehicles.service'
import { Depot } from '@shared/models/depot.model'
import { Device } from '@shared/models/device.model'
import { Role } from '@shared/models/role.model'
import { TeamType } from '@shared/models/team.model'
import { UserGroup } from '@shared/models/user-group.model'
import { User, UserInList, UserInListModel } from '@shared/models/user.model'
import { VehicleGroup } from '@shared/models/vehicle-group.model'
import { Vehicle } from '@shared/models/vehicle.model'
import { DevicesService } from '../devices/devices.service'
import {
  AdditionalScopeSubType,
  NotificationAdditionalScopeItemLabel,
  NotificationRecipientScopeItemLabel,
  NotificationScopeItem,
  NotificationScopeItemLabel,
  RecipientType,
  ScopeSubType,
} from './shared/notification.model'
import { Asset } from '@shared/models/asset.model'
import { AssetsService } from '@app/modules/client/assets/services/assets.service'
import { ResellerService } from '@app/modules/reseller/shared/services/reseller.service'
import { DeviceSensorsApiService } from '@shared/services/device-sensors-api.service'
import { Sensor } from '@shared/models/sensor.model'
import { Driver } from '@shared/models/driver.model'

@Injectable()
export class NotificationFormService {
  private teamId: number
  private clientId: number
  private resellerId: number
  private roles: Role[]
  private eventTeam: TeamType
  private teamType: TeamType

  constructor(
    private readonly clientService: ClientsService,
    private readonly adminTeamService: AdminTeamService,
    private readonly userGroupsService: UserGroupsService,
    private readonly vehicleService: VehiclesService,
    private readonly devicesService: DevicesService,
    private readonly geofencesService: GeofencesApiService,
    private readonly assetsService: AssetsService,
    private readonly resellerService: ResellerService,
    private readonly deviceSensorsService: DeviceSensorsApiService,
  ) {}

  private getUserList(isRecipients = false): Observable<UserInListModel[]> {
    let clientUserList: Observable<UserInListModel[]>
    const clientUserListForAdmin =
      !isRecipients && this.eventTeam === TeamType.Client
        ? this.clientService.getClientUserList(this.clientId)
        : this.adminTeamService.getUserList()

    if (isRecipients && this.teamType === TeamType.Admin) {
      clientUserList = this.adminTeamService.getUserList()
    } else if (isRecipients && this.teamType === TeamType.Reseller) {
      clientUserList = this.resellerService.getResellerUsersList(this.resellerId)
    } else {
      clientUserList = this.clientService.getClientUserList(this.clientId)
    }

    const userList = {
      [TeamType.Client]: clientUserList,
      [TeamType.Admin]: clientUserListForAdmin,
      [TeamType.Reseller]: this.resellerService.getResellerUsersList(this.clientId),
    }
    const userList$ = userList[this.eventTeam]

    return userList$
  }

  private getRecipientItems(recipientType: RecipientType): Observable<unknown> {
    switch (recipientType) {
      case RecipientType.Any:
        return of([])
      case RecipientType.UserList:
        return this.getUserList(true)
      case RecipientType.Role:
        return of(this.roles)
      case RecipientType.UserGroupList:
        return this.userGroupsService.getUserGroupList(['id', 'name', 'team'])
    }
  }

  getRecepientScopeItems(
    scope: RecipientType,
    teamId: number,
    clientId: number,
    roles: Role[],
    eventTeam: TeamType,
    teamType: TeamType,
    resellerId: number,
  ): Observable<NotificationScopeItem[]> {
    this.teamId = teamId
    this.clientId = clientId
    this.roles = roles
    this.eventTeam = eventTeam
    this.teamType = teamType
    this.resellerId = resellerId

    const scopeValueField: { [key in RecipientType]?: NotificationRecipientScopeItemLabel } = {
      [RecipientType.Role]: 'displayName',
      [RecipientType.UserList]: 'fullName',
      [RecipientType.UserGroupList]: 'name',
    }

    return this.getRecipientItems(scope).pipe(
      map((notificationScopeItems: (Role | User | UserGroup)[]) =>
        notificationScopeItems?.map((notificationScopeItem) => this.transfromScopeItem(notificationScopeItem, scopeValueField, scope)),
      ),
      delay(0),
    )
  }

  getNotificationScopeItems(
    scope: ScopeSubType,
    teamId: number,
    clientId: number,
    roles: Role[],
    eventTeam: TeamType,
    teamType: TeamType,
    isCameras: boolean,
  ): Observable<NotificationScopeItem[]> {
    this.teamId = teamId
    this.clientId = clientId
    this.roles = roles
    this.eventTeam = eventTeam
    this.teamType = teamType

    const scopeValueField: { [key in ScopeSubType]?: NotificationScopeItemLabel } = {
      [ScopeSubType.Vehicle]: 'regNo',
      [ScopeSubType.Depot]: 'name',
      [ScopeSubType.Group]: 'name',
      [ScopeSubType.UserGroup]: 'name',
      [ScopeSubType.User]: 'fullName',
      [ScopeSubType.Device]: 'imei',
      [ScopeSubType.Role]: 'displayName',
      [ScopeSubType.Asset]: 'name',
      [ScopeSubType.Sensor]: 'label',
      [ScopeSubType.Driver]: 'fullName',
    }

    return this.getScopeItems(scope, isCameras).pipe(
      map((notificationScopeItems: (Vehicle | VehicleGroup | Depot | UserGroup | Device | User | Asset | Sensor | Driver)[]) =>
        notificationScopeItems.map((notificationScopeItem) => this.transfromScopeItem(notificationScopeItem, scopeValueField, scope)),
      ),
      delay(0),
    )
  }

  private transfromScopeItem(
    scopeItem: Vehicle | VehicleGroup | Depot | UserGroup | Geofence | GeofenceGroup | Device | User | Role | Asset | Sensor | Driver,
    scopeValueField: {
      [key in ScopeSubType | AdditionalScopeSubType | RecipientType]?:
        | NotificationScopeItemLabel
        | NotificationAdditionalScopeItemLabel
        | NotificationRecipientScopeItemLabel
    },
    scope: ScopeSubType | AdditionalScopeSubType | RecipientType,
  ): NotificationScopeItem {
    const label = (
      'sensorId' in scopeItem ? scopeItem[scopeValueField[scope]] || scopeItem['sensorId'] : scopeItem[scopeValueField[scope]]
    ) as NotificationScopeItemLabel | NotificationAdditionalScopeItemLabel | NotificationRecipientScopeItemLabel
    const item = { id: scopeItem.id, label, team: scopeItem.team }

    return item
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getScopeItems(scope: ScopeSubType, isCameras?: boolean): Observable<any> {
    switch (scope) {
      case ScopeSubType.Any:
        return of([])
      case ScopeSubType.Vehicle:
        return this.vehicleService.getVehicleList(
          0,
          1000,
          null,
          { teamId: this.teamId, teamType: TeamType.Client, ...(isCameras && { hasCameras: true }) },
          ['id', 'regNo', 'team'],
          true,
        )
      case ScopeSubType.Depot:
        return this.vehicleService.getDepotList({ teamId: this.teamId }, ['id', 'name', 'team'])
      case ScopeSubType.Group:
        return this.vehicleService.getVehicleGroupList({ teamId: this.teamId }, ['id', 'name', 'team'])
      case ScopeSubType.Role:
        return of(this.roles)
      case ScopeSubType.Device:
        return this.devicesService.getDeviceList(0, 1000, null, { teamId: this.teamId }, ['id', 'imei', 'team'])
      case ScopeSubType.UserGroup:
        return this.userGroupsService.getUserGroupList(['id', 'name', 'team'], { teamId: this.teamId })
      case ScopeSubType.TeamType:
        return of([])
      case ScopeSubType.User:
        return this.getUserList()
      case ScopeSubType.Asset:
        return this.getAssetList()
      case ScopeSubType.Sensor:
        return this.getSensorList()
      case ScopeSubType.Driver:
        return this.getDriverList()
    }
  }

  getAdditionalNotificationScopeItems(scope: AdditionalScopeSubType, teamId: number): Observable<NotificationScopeItem[]> {
    this.teamId = teamId

    const scopeValueField: { [key in AdditionalScopeSubType]?: NotificationAdditionalScopeItemLabel } = {
      [AdditionalScopeSubType.Area]: 'name',
      [AdditionalScopeSubType.AreaGroup]: 'name',
    }

    return this.getAdditionalScopeItems(scope).pipe(
      map((notificationScopeItems: (Geofence | GeofenceGroup)[]) =>
        notificationScopeItems.map((notificationScopeItem) => this.transfromScopeItem(notificationScopeItem, scopeValueField, scope)),
      ),
      delay(0),
    )
  }

  private getAdditionalScopeItems(scope: AdditionalScopeSubType): Observable<(Geofence | GeofenceGroup | unknown)[]> {
    switch (scope) {
      case AdditionalScopeSubType.All:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return of([])
      case AdditionalScopeSubType.Any:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return of([])
      case AdditionalScopeSubType.Area:
        return this.geofencesService.getGeofencesList({ teamId: this.teamId, status: GeofenceStatus.Active }, ['id', 'name', 'team'])
      case AdditionalScopeSubType.AreaGroup:
        return this.geofencesService.getGeofencesGroupList({ teamId: this.teamId }, ['id', 'name', 'team'])
    }
  }

  getTimeDurationLabel(name: string): string {
    const isStanding = this.isSubstringIncluded(name, 'standing')
    const isIdling = this.isSubstringIncluded(name, 'idling')
    const isMissed = this.isSubstringIncluded(name, 'missed')

    switch (true) {
      case isStanding:
        return 'FIELDS.STANDING_TIME_DURATION'
      case isIdling:
        return 'FIELDS.IDLING_TIME_DURATION'
      case isMissed:
        return 'FIELDS.ASSET_MISSED_DURATION'
      default:
        return 'FIELDS.DRIVING_TIME_DURATION'
    }
  }

  private isSubstringIncluded(subject: string, substring: string): boolean {
    return subject.toLocaleLowerCase().split('_').includes(substring)
  }

  private getAssetList(): Observable<Asset[]> {
    return this.assetsService.getAssetList()
  }

  private getSensorList(): Observable<Sensor[]> {
    return this.deviceSensorsService.getAllSensorsList(null, ['id', 'label', 'team', 'sensorId'])
  }

  private getDriverList(): Observable<UserInList[]> {
    return this.clientService.getClientUserList(this.clientId, { role: 'driver' })
  }
}
